import { IonContent, IonPage, IonSpinner, useIonAlert } from "@ionic/react";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";
import Button from "../components/Button";
import Header from "../components/Header";
import { BillingContext } from "../context/BillingContext";
import { initiateBankTransfer, checkPaymentStatus } from "../services";

interface VirtualAccountData {
  transaction_id: number;
  opay_reference: string;
  account_number: string;
  bank_name: string;
  expiry_timestamp: number;
  server_time: number;
  amount: number;
}

const BankTransfer: React.FC = () => {
  const history = useHistory();
  const [presentAlert] = useIonAlert();
  const { state } = useContext(BillingContext);

  const [vaData, setVaData] = useState<VirtualAccountData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<string>("");
  const [expired, setExpired] = useState(false);
  const [completed, setCompleted] = useState(false);

  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  const handleGoBack = () => {
    history.goBack();
  };

  // Guard: redirect if no wash_id
  useEffect(() => {
    if (!state.wash_id) {
      history.replace("/home");
    }
  }, []);

  // Initiate bank transfer on mount
  useEffect(() => {
    if (!state.wash_id) return;
    initiateTransfer();

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  const initiateTransfer = async () => {
    setLoading(true);
    setError(null);
    setExpired(false);

    try {
      const response = await initiateBankTransfer({
        estimated_sum: state.total,
        wash_id: state.wash_id,
      });

      if (response.success && response.data) {
        setVaData(response.data);
        startCountdown(response.data.expiry_timestamp, response.data.server_time);
        startPolling(response.data.opay_reference);
      } else {
        setError(response.message || "Failed to generate virtual account");
      }
    } catch (err: any) {
      setError(err.message || "Unable to initiate bank transfer");
    } finally {
      setLoading(false);
    }
  };

  const startCountdown = (expiryTimestamp: number, serverTime: number) => {
    if (countdownRef.current) clearInterval(countdownRef.current);

    // Calculate offset between server time and local time
    const timeOffset = serverTime - Date.now();

    countdownRef.current = setInterval(() => {
      const now = Date.now() + timeOffset;
      const remaining = Math.max(0, expiryTimestamp - now);

      if (remaining <= 0) {
        setExpired(true);
        setCountdown("00:00");
        if (countdownRef.current) clearInterval(countdownRef.current);
        if (pollRef.current) clearInterval(pollRef.current);
        return;
      }

      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      setCountdown(`${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`);
    }, 1000);
  };

  const startPolling = (reference: string) => {
    if (pollRef.current) clearInterval(pollRef.current);

    pollRef.current = setInterval(async () => {
      try {
        const response = await checkPaymentStatus(reference);

        if (response.success && response.data) {
          if (response.data.status === "completed") {
            setCompleted(true);
            if (pollRef.current) clearInterval(pollRef.current);
            if (countdownRef.current) clearInterval(countdownRef.current);

            presentAlert({
              header: "Payment Successful!",
              message: response.data.token_id
                ? `Your wash token is: ${response.data.token_id}`
                : "Your payment has been confirmed.",
              buttons: [
                {
                  text: "OK",
                  handler: () => history.replace("/home"),
                },
              ],
              backdropDismiss: false,
            });
          }
        }
      } catch (err) {
        // Silent fail on poll — will retry next interval
      }
    }, 5000);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
    }).format(amount);
  };

  if (loading) {
    return (
      <IonPage>
        <IonContent>
          <Header title="Bank Transfer" onClick={handleGoBack} />
          <div style={{ padding: "16px", textAlign: "center", marginTop: "40px" }}>
            <IonSpinner name="crescent" />
            <p>Generating virtual account...</p>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (error) {
    return (
      <IonPage>
        <IonContent>
          <Header title="Bank Transfer" onClick={handleGoBack} />
          <div style={{ padding: "16px", textAlign: "center", marginTop: "40px" }}>
            <p style={{ color: "#dc3545" }}>{error}</p>
            <div style={{ paddingTop: "20px" }}>
              <Button title="Retry" onClick={() => initiateTransfer()} />
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (expired && !completed) {
    return (
      <IonPage>
        <IonContent>
          <Header title="Bank Transfer" onClick={handleGoBack} />
          <div style={{ padding: "16px", textAlign: "center", marginTop: "40px" }}>
            <p style={{ color: "#dc3545", fontWeight: "bold" }}>
              Virtual account has expired.
            </p>
            <div style={{ paddingTop: "20px" }}>
              <Button title="Generate New Account" onClick={() => initiateTransfer()} />
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonContent>
        <Header title="Bank Transfer" onClick={handleGoBack} />
        <div style={{ padding: "16px" }}>
          <p>Transfer the exact amount to the account below:</p>
          <br />
          <p>
            Account Number: <strong>{vaData?.account_number}</strong>
          </p>
          <p>
            Bank Name: <strong>{vaData?.bank_name}</strong>
          </p>
          <p>
            Amount: <strong>{vaData ? formatCurrency(vaData.amount) : ""}</strong>
          </p>
          <br />
          <p style={{ textAlign: "center", fontSize: "24px", fontWeight: "bold" }}>
            {countdown}
          </p>
          <p style={{ textAlign: "center", fontSize: "12px", color: "#666" }}>
            Time remaining
          </p>
          <br />
          <p style={{ fontSize: "14px", color: "#555" }}>
            Your payment will be confirmed automatically once the transfer is received.
          </p>
          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <IonSpinner name="dots" />
            <p style={{ fontSize: "12px", color: "#888" }}>Waiting for payment...</p>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default BankTransfer;
